import { useEffect, useRef, useState } from "react";
import type { F9SyncStatus } from "../../models";
import { fetchF9SyncStatus } from "../../api/clientV2";
import { onF9Refresh, onScheduleMutated } from "../../api/scheduleMutations";

/**
 * F9-sync status for the active schedule: whether running F9 Simulate would
 * change anything. Refetches when the version switches (different working DB)
 * or any mutation fires (clientV2 interceptor → scheduleMutations). The check
 * is cached server-side by a change_log watermark, so the post-mutation refetch
 * is cheap. Returns null while loading / before a schedule exists.
 */
export function useF9Sync(
  scheduleId: number | null,
  activeVersion: number | null,
): F9SyncStatus | null {
  const [status, setStatus] = useState<F9SyncStatus | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);
  // Marks the NEXT fetch as cache-bypassing (set when the F9 modal opens —
  // the data is unchanged, so only a forced recompute updates the count).
  const forceNext = useRef(false);

  useEffect(() => onScheduleMutated(() => setRefetchTick((t) => t + 1)), []);
  useEffect(
    () =>
      onF9Refresh(() => {
        forceNext.current = true;
        setRefetchTick((t) => t + 1);
      }),
    [],
  );

  // Drop the old schedule's badge on a version switch so we never show the
  // previous DB's sync state while the new one loads (only on schedule/version
  // change — NOT on every mutation tick, which would flicker the badge).
  useEffect(() => {
    setStatus(null);
  }, [scheduleId, activeVersion]);

  useEffect(() => {
    if (scheduleId == null) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    const force = forceNext.current;
    forceNext.current = false;
    fetchF9SyncStatus(scheduleId, force)
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        /* keep last value on error — the badge isn't worth a hard failure */
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, activeVersion, refetchTick]);

  return status;
}
